import { MemoryPackWriter } from "./MemoryPackWriter.js";
import { MemoryPackReader } from "./MemoryPackReader.js";
import { ModelParameterDto } from "./ModelParameterDto.js";

export class NodeDto {
    nodeId: bigint;
    nodePath: string | null;
    nodeType: number;
    modelId: string;
    cadModelCategoryId: string;
    cadId: string | null;
    cadFamily: string | null;
    cadName: string | null;
    cadMaterial: string | null;
    cadReferenceId: string | null;
    isStructuralMaterial: boolean | null;
    scopeId: number | null;
    componentCount: number;
    quantity: number | null;
    length: number | null;
    area: number | null;
    volume: number | null;
    thickness: number | null;
    originalQuantity: number | null;
    originalLength: number | null;
    originalArea: number | null;
    originalVolume: number | null;
    originalThickness: number | null;
    omniClassNumber: string | null;
    omniClassTitle: string | null;
    uniFormatNumber: string | null;
    uniFormatTitle: string | null;
    masterFormatNumber: string | null;
    masterFormatTitle: string | null;
    multiplierInput: string | null;
    multiplierValue: number | null;
    notes: string | null;
    carbonImpactValue: number | null;
    carbonImpactInput: string | null;
    carbonImpactNotes: string | null;
    hasChildCarbonImpactOverride: boolean;
    systemId: string | null;
    subSystemId: string | null;
    freeText1: string | null;
    freeText2: string | null;
    freeText3: string | null;
    co2MaterialTypeSpec: string | null;
    modelParameters: (ModelParameterDto | null)[] | null;

    constructor() {
        this.nodeId = 0n;
        this.nodePath = null;
        this.nodeType = 0;
        this.modelId = "00000000-0000-0000-0000-000000000000";
        this.cadModelCategoryId = "00000000-0000-0000-0000-000000000000";
        this.cadId = null;
        this.cadFamily = null;
        this.cadName = null;
        this.cadMaterial = null;
        this.cadReferenceId = null;
        this.isStructuralMaterial = null;
        this.scopeId = null;
        this.componentCount = 0;
        this.quantity = null;
        this.length = null;
        this.area = null;
        this.volume = null;
        this.thickness = null;
        this.originalQuantity = null;
        this.originalLength = null;
        this.originalArea = null;
        this.originalVolume = null;
        this.originalThickness = null;
        this.omniClassNumber = null;
        this.omniClassTitle = null;
        this.uniFormatNumber = null;
        this.uniFormatTitle = null;
        this.masterFormatNumber = null;
        this.masterFormatTitle = null;
        this.multiplierInput = null;
        this.multiplierValue = null;
        this.notes = null;
        this.carbonImpactValue = null;
        this.carbonImpactInput = null;
        this.carbonImpactNotes = null;
        this.hasChildCarbonImpactOverride = false;
        this.systemId = null;
        this.subSystemId = null;
        this.freeText1 = null;
        this.freeText2 = null;
        this.freeText3 = null;
        this.co2MaterialTypeSpec = null;
        this.modelParameters = null;

    }

    static serialize(value: NodeDto | null): Uint8Array {
        const writer = MemoryPackWriter.getSharedInstance();
        this.serializeCore(writer, value);
        return writer.toArray();
    }

    static serializeCore(writer: MemoryPackWriter, value: NodeDto | null): void {
        if (value == null) {
            writer.writeNullObjectHeader();
            return;
        }

        writer.writeObjectHeader(43);
        writer.writeInt64(value.nodeId);
        writer.writeString(value.nodePath);
        writer.writeInt32(value.nodeType);
        writer.writeGuid(value.modelId);
        writer.writeGuid(value.cadModelCategoryId);
        writer.writeString(value.cadId);
        writer.writeString(value.cadFamily);
        writer.writeString(value.cadName);
        writer.writeString(value.cadMaterial);
        writer.writeString(value.cadReferenceId);
        writer.writeNullableBoolean(value.isStructuralMaterial);
        writer.writeNullableInt32(value.scopeId);
        writer.writeInt32(value.componentCount);
        writer.writeNullableFloat64(value.quantity);
        writer.writeNullableFloat64(value.length);
        writer.writeNullableFloat64(value.area);
        writer.writeNullableFloat64(value.volume);
        writer.writeNullableFloat64(value.thickness);
        writer.writeNullableFloat64(value.originalQuantity);
        writer.writeNullableFloat64(value.originalLength);
        writer.writeNullableFloat64(value.originalArea);
        writer.writeNullableFloat64(value.originalVolume);
        writer.writeNullableFloat64(value.originalThickness);
        writer.writeString(value.omniClassNumber);
        writer.writeString(value.omniClassTitle);
        writer.writeString(value.uniFormatNumber);
        writer.writeString(value.uniFormatTitle);
        writer.writeString(value.masterFormatNumber);
        writer.writeString(value.masterFormatTitle);
        writer.writeString(value.multiplierInput);
        writer.writeNullableFloat64(value.multiplierValue);
        writer.writeString(value.notes);
        writer.writeNullableFloat64(value.carbonImpactValue);
        writer.writeString(value.carbonImpactInput);
        writer.writeString(value.carbonImpactNotes);
        writer.writeBoolean(value.hasChildCarbonImpactOverride);
        writer.writeNullableGuid(value.systemId);
        writer.writeNullableGuid(value.subSystemId);
        writer.writeString(value.freeText1);
        writer.writeString(value.freeText2);
        writer.writeString(value.freeText3);
        writer.writeString(value.co2MaterialTypeSpec);
        writer.writeArray(value.modelParameters, (writer, x) => ModelParameterDto.serializeCore(writer, x));

    }

    static serializeArray(value: (NodeDto | null)[] | null): Uint8Array {
        const writer = MemoryPackWriter.getSharedInstance();
        this.serializeArrayCore(writer, value);
        return writer.toArray();
    }

    static serializeArrayCore(writer: MemoryPackWriter, value: (NodeDto | null)[] | null): void {
        writer.writeArray(value, (writer, x) => NodeDto.serializeCore(writer, x));
    }

    static deserialize(buffer: ArrayBuffer): NodeDto | null {
        return this.deserializeCore(new MemoryPackReader(buffer));
    }

    static deserializeCore(reader: MemoryPackReader): NodeDto | null {
        const [ok, count] = reader.tryReadObjectHeader();
        if (!ok) {
            return null;
        }

        const value = new NodeDto();
        if (count == 43) {
            value.nodeId = reader.readInt64();
            value.nodePath = reader.readString();
            value.nodeType = reader.readInt32();
            value.modelId = reader.readGuid();
            value.cadModelCategoryId = reader.readGuid();
            value.cadId = reader.readString();
            value.cadFamily = reader.readString();
            value.cadName = reader.readString();
            value.cadMaterial = reader.readString();
            value.cadReferenceId = reader.readString();
            value.isStructuralMaterial = reader.readNullableBoolean();
            value.scopeId = reader.readNullableInt32();
            value.componentCount = reader.readInt32();
            value.quantity = reader.readNullableFloat64();
            value.length = reader.readNullableFloat64();
            value.area = reader.readNullableFloat64();
            value.volume = reader.readNullableFloat64();
            value.thickness = reader.readNullableFloat64();
            value.originalQuantity = reader.readNullableFloat64();
            value.originalLength = reader.readNullableFloat64();
            value.originalArea = reader.readNullableFloat64();
            value.originalVolume = reader.readNullableFloat64();
            value.originalThickness = reader.readNullableFloat64();
            value.omniClassNumber = reader.readString();
            value.omniClassTitle = reader.readString();
            value.uniFormatNumber = reader.readString();
            value.uniFormatTitle = reader.readString();
            value.masterFormatNumber = reader.readString();
            value.masterFormatTitle = reader.readString();
            value.multiplierInput = reader.readString();
            value.multiplierValue = reader.readNullableFloat64();
            value.notes = reader.readString();
            value.carbonImpactValue = reader.readNullableFloat64();
            value.carbonImpactInput = reader.readString();
            value.carbonImpactNotes = reader.readString();
            value.hasChildCarbonImpactOverride = reader.readBoolean();
            value.systemId = reader.readNullableGuid();
            value.subSystemId = reader.readNullableGuid();
            value.freeText1 = reader.readString();
            value.freeText2 = reader.readString();
            value.freeText3 = reader.readString();
            value.co2MaterialTypeSpec = reader.readString();
            value.modelParameters = reader.readArray(reader => ModelParameterDto.deserializeCore(reader));

        }
        else if (count > 43) {
            throw new Error("Current object's property count is larger than type schema, can't deserialize about versioning.");
        }
        else {
            if (count == 0) return value;
            value.nodeId = reader.readInt64(); if (count == 1) return value;
            value.nodePath = reader.readString(); if (count == 2) return value;
            value.nodeType = reader.readInt32(); if (count == 3) return value;
            value.modelId = reader.readGuid(); if (count == 4) return value;
            value.cadModelCategoryId = reader.readGuid(); if (count == 5) return value;
            value.cadId = reader.readString(); if (count == 6) return value;
            value.cadFamily = reader.readString(); if (count == 7) return value;
            value.cadName = reader.readString(); if (count == 8) return value;
            value.cadMaterial = reader.readString(); if (count == 9) return value;
            value.cadReferenceId = reader.readString(); if (count == 10) return value;
            value.isStructuralMaterial = reader.readNullableBoolean(); if (count == 11) return value;
            value.scopeId = reader.readNullableInt32(); if (count == 12) return value;
            value.componentCount = reader.readInt32(); if (count == 13) return value;
            value.quantity = reader.readNullableFloat64(); if (count == 14) return value;
            value.length = reader.readNullableFloat64(); if (count == 15) return value;
            value.area = reader.readNullableFloat64(); if (count == 16) return value;
            value.volume = reader.readNullableFloat64(); if (count == 17) return value;
            value.thickness = reader.readNullableFloat64(); if (count == 18) return value;
            value.originalQuantity = reader.readNullableFloat64(); if (count == 19) return value;
            value.originalLength = reader.readNullableFloat64(); if (count == 20) return value;
            value.originalArea = reader.readNullableFloat64(); if (count == 21) return value;
            value.originalVolume = reader.readNullableFloat64(); if (count == 22) return value;
            value.originalThickness = reader.readNullableFloat64(); if (count == 23) return value;
            value.omniClassNumber = reader.readString(); if (count == 24) return value;
            value.omniClassTitle = reader.readString(); if (count == 25) return value;
            value.uniFormatNumber = reader.readString(); if (count == 26) return value;
            value.uniFormatTitle = reader.readString(); if (count == 27) return value;
            value.masterFormatNumber = reader.readString(); if (count == 28) return value;
            value.masterFormatTitle = reader.readString(); if (count == 29) return value;
            value.multiplierInput = reader.readString(); if (count == 30) return value;
            value.multiplierValue = reader.readNullableFloat64(); if (count == 31) return value;
            value.notes = reader.readString(); if (count == 32) return value;
            value.carbonImpactValue = reader.readNullableFloat64(); if (count == 33) return value;
            value.carbonImpactInput = reader.readString(); if (count == 34) return value;
            value.carbonImpactNotes = reader.readString(); if (count == 35) return value;
            value.hasChildCarbonImpactOverride = reader.readBoolean(); if (count == 36) return value;
            value.systemId = reader.readNullableGuid(); if (count == 37) return value;
            value.subSystemId = reader.readNullableGuid(); if (count == 38) return value;
            value.freeText1 = reader.readString(); if (count == 39) return value;
            value.freeText2 = reader.readString(); if (count == 40) return value;
            value.freeText3 = reader.readString(); if (count == 41) return value;
            value.co2MaterialTypeSpec = reader.readString(); if (count == 42) return value;
            value.modelParameters = reader.readArray(reader => ModelParameterDto.deserializeCore(reader)); if (count == 43) return value;

        }
        return value;
    }

    static deserializeArray(buffer: ArrayBuffer): (NodeDto | null)[] | null {
        return this.deserializeArrayCore(new MemoryPackReader(buffer));
    }

    static deserializeArrayCore(reader: MemoryPackReader): (NodeDto | null)[] | null {
        return reader.readArray(reader => NodeDto.deserializeCore(reader));
    }
}
